import type { ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';

/**
 * A small label with a colour that means something.
 *
 * Five tones and not a palette: `ok` is done, `warn` is waiting or doubtful, `danger`
 * needs a person, `info` is a fact worth noticing, `neutral` is everything routine. The
 * dot is for rows where the colour alone would be too quiet.
 */
export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'accent';

const TONES: Record<Tone, string> = {
  neutral: 'bg-paper-2 text-ink-2 ring-line',
  ok: 'bg-ok-soft text-ok ring-ok/20',
  warn: 'bg-warn-soft text-warn ring-warn/20',
  danger: 'bg-danger-soft text-danger ring-danger/20',
  info: 'bg-info-soft text-info ring-info/20',
  accent: 'bg-accent-soft text-accent-2 ring-accent/20',
};

const DOTS: Record<Tone, string> = {
  neutral: 'bg-ink-4',
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  accent: 'bg-accent',
};

export function Badge({
  tone = 'neutral',
  dot = false,
  className,
  children,
  title,
  wrap = false,
}: {
  tone?: Tone;
  dot?: boolean;
  className?: string;
  children: ReactNode;
  title?: string;
  /** Let a long label break into two lines, for a badge in a narrow table column. */
  wrap?: boolean;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[12px] font-medium ring-1 ring-inset',
        wrap ? 'leading-tight' : 'shrink-0 whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {dot ? <span aria-hidden="true" className={cn('size-1.5 rounded-full', DOTS[tone])} /> : null}
      {children}
    </span>
  );
}
